import loadable from '@loadable/component';
import BpkGraphicPromo, {
  TEXT_ALIGN,
} from '@skyscanner-internal/backpack-web/bpk-component-graphic-promotion';
import { ACTION_TYPE } from '@skyscanner-internal/landing-pages-shared-constants/constants';
import { redirect } from 'saddlebag-browser';

import logMiniEventHelper from '../../mini-event/logMiniEventHelper';
import FooterBlurb from '../common/FooterBlurb';
import { useMBDContext } from '../common/MBD/useMBDContext';

import type { GraphicPromotionProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/GraphicPromotionProps';

import STYLES from './GraphicPromotion.module.scss';

const IntersectionObserverWrapper = loadable(
  () => import('../IntersectionObserverWrapper/IntersectionObserverWrapper'),
);

const gradient = `linear-gradient(90deg, rgba(22, 22, 22, 0.45) 0%, rgba(22, 22, 22, 0) 100%)`;

const GraphicPromotion = ({
  buttonText,
  buttonUri,
  disclaimer,
  hasOverlay = true,
  headline,
  imageAlign = 'center',
  imageUri,
  isCreatorPage = false,
  subheading,
  textAlign = TEXT_ALIGN.start,
}: GraphicPromotionProps & { isCreatorPage?: boolean }) => {
  const backgroundImage = hasOverlay
    ? `${gradient}, url(${imageUri})`
    : `url(${imageUri})`;

  const { buildMBDParam, rawComponentName } = useMBDContext();

  const mbdParams = {
    component_name: 'creators_creator_graphic_promotion',
    string_properties: {
      vertical: 'creators',
      page_type: 'creator-destination',
    },
  };

  return (
    <div
      className={STYLES.GraphicPromotion}
      data-tracking-element-id={
        isCreatorPage ? mbdParams.component_name : rawComponentName
      }
      data-tracking-common-params={
        isCreatorPage
          ? JSON.stringify(mbdParams)
          : buildMBDParam(rawComponentName, {
              buttonUri,
            })
      }
    >
      <BpkGraphicPromo
        buttonText={buttonText}
        headline={headline}
        subheading={subheading}
        onClick={() => redirect(buttonUri)}
        style={{
          backgroundImage,
          backgroundPositionX: imageAlign,
        }}
        textAlign={textAlign}
        invertVertically
      />
      {disclaimer && (
        <div className={STYLES.GraphicPromotion__disclaimer}>
          <FooterBlurb disclaimerCopy={disclaimer} />
        </div>
      )}
    </div>
  );
};

export default (props: GraphicPromotionProps) => {
  const { rawComponentName } = useMBDContext();

  return (
    <IntersectionObserverWrapper
      onElementSeen={() =>
        logMiniEventHelper({
          action_type: ACTION_TYPE.COMPONENT_VISIBLE,
          component_name: rawComponentName.toUpperCase(),
        })
      }
    >
      <GraphicPromotion {...props} />
    </IntersectionObserverWrapper>
  );
};
